import { DeviceType } from "common/types";
import { Moment } from "moment";

export interface INodeResult {
  ID: number;
  Name: string;
  Result: INodeData[];
}
export interface IInstallation {
  MeasuringDeviceID: number;
  Registers: IRegister[];
  DeviceType: DeviceType;
  DeviceTypeDisplay: string;
  ExternalKey: string;
  NodeID: number;
  MeasuringPointID: number;
  From: number;
  To: number;
}
export interface IInstallationExtended extends IInstallation {
  data: IInstallationDataResult[];
  currentMonthCost: number;
  currentMonthEmission: number;
  comparisonData: IInstallationDataResult[] | undefined;
}
export interface ISetInstallations {
  installationsExtended: IInstallationExtended[];
  totalMonthCost: number;
  totalMonthEmission: number;
  currency: string | undefined;
}
export interface IInstallationData {
  ID: number;
  Name: string;
  Result: IInstallationDataResult[];
}
export interface IMeasuringPoint {
  ID: number;
  Name: string;
}
export interface IDetailedInstallation {
  data: IInstallationDataResult[];
  averageTemperature: number | null;
  averageRelativeHumidity: number | null;
  averageConsumption: number | null;
  emissions: number | null;
  cost: number | null;
  totalConsumption: number | null;
  previousData: IInstallationDataResult[] | null;
}
export interface ISelectedInstallation {
  displayName: string | undefined;
  detailedInstallation: IDetailedInstallation;
  measuringPoints: IMeasuringPoint[] | null;
}
export interface IInstallationDataResult {
  Utl: UtlType;
  Func: string;
  Unit: string;
  Values: IValues[];
}

export interface IRegister {
  Register: number;
  UtilityCode: UtlType;
}

export interface INodeData {
  Func: string;
  Values: INodeValues[];
}

export interface INodeValues {
  Time: number;
  Value: number;
}

export interface IUtility {
  utlType: UtlType;
  aggregate: string;
}

export enum Grouping {
  Apartment = "apartment",
  MeasuringPoint = "measuringpoint",
}

export interface IUnixTimeObject {
  period: PeriodType;
  from: number;
  to: number;
}

export interface ITemperatureHistory {
  Time: number;
  Value: number;
}
export interface INodeWithParents {
  ParentNodeID: string | null;
  ID: number;
  Name: string;
  NodeType: INodeType;
  ParentNodes: IParentNodes[];
}
export interface IParentNodes {
  ParentNodeID: string | null;
  ID: number;
  Name: string;
  NodeType: INodeType;
}

export interface INodeType {
  ID: number;
  Code: number;
  Name: string;
}

export interface INodeTree {
  ParentNodes: IParentNodes[];
  ParentNodeID: string | null;
  ID: number;
  Name: string;
  NodeType: INodeType;
}
export enum DataTypes {
  Consumption = 0,
  Price = 1,
  Co2 = 2,
}
export enum UtlType {
  Electricity = "ELEC",
  HotWater = "HW",
  ColdWater = "CW",
  RelativeHumidity = "RH",
  HeatEnergy = "HEAT",
  Temperature = "T",
  Undefined = "",
  HeatCostAllocator = "HCA",
  HeatCostAllocatorE = "HCAE",
}
export interface IValues {
  Time: number;
  Value: number;
  ConvertedTime?: Moment;
}

export interface IDateHandlerObject {
  period: string;
  from: Moment;
  to: Moment;
}

export type PeriodType = "week" | "year" | "month" | "days";

export interface ISelectedDatasets {
  currentData: IInstallationDataResult;
  previousData: IInstallationDataResult | undefined;
}

export type UnitType = "kwh" | "kg" | "g" | "liter";

export interface IBillingResult {
  ID: number;
  Start: number;
  End: number;
  NodeID: number;
  ObjectNumber: string;
  ContractCode: string | null;
  Billing: {
    ID: number;
    Name: string;
  };
  Parts: IBillingPart[];
  Alerts: IBillingAlert[];
}

export interface IBillingPart {
  Code: string;
  Name: string;
  Items: IBillingItem[];
  Vat: number | null;
  Rounding: number | null;
}

export interface IBillingItem {
  PriceComponent: PriceComponent;
  Start: number;
  End: number;
  Quantity: number | null;
  QuantityUnit: string;
  Rate: number | null;
  RateUnit: string | null;
  RateVat: number | null;
  Total: number | null;
  TotalVat: number | null;
  MeasuringDevice: MeasuringDevice | null;
  Normal: number | null;
  Percentage: number | null;
  Status: BillingItemStatus;
}

export interface PriceComponent {
  Type: string;
  Name: string;
}

export interface MeasuringDevice {
  SerialNumber: string;
  ReadingStart: number | null;
  ReadingEnd: number | null;
}

export interface IBillingAlert {
  Severity: BillingAlertSeverity;
  Code: string;
  Text: string;
}

export enum BillingAlertSeverity {
  Info = "Info",
  Warning = "Warning",
  Error = "Error",
}

export enum BillingItemStatus {
  Ok = "OK",
  Failed = "NOK",
}
